/**
 * L'agrément du client, la garantie de paiement, et le planning partagé.
 *
 * Décidé avec le patron le 30 octobre 2026, cinquième volet du phasage. La
 * loi du 31 décembre 1975 relative à la sous-traitance demande deux choses à
 * l'entreprise qui sous-traite, et ce module dit, marché par marché, ce qui
 * manque :
 *
 *   · **présenter le sous-traitant au client** (art. 3), qui l'accepte et
 *     agrée ses conditions de paiement. Sans cet agrément, l'entreprise ne
 *     peut pas opposer le contrat au sous-traitant ;
 *   · **garantir son paiement** (art. 14) : en marché privé, une caution
 *     bancaire, ou une délégation de paiement — le client le paie lui-même —
 *     faute de quoi le sous-traité est nul. En marché public, le
 *     sous-traitant accepté est payé directement par l'acheteur (Code de la
 *     commande publique, art. L2193-11).
 *
 * Sous délégation ou paiement direct, l'argent va du client au sous-traitant
 * sans passer par l'entreprise : un règlement « payé par le client » ne sort
 * pas de sa trésorerie.
 *
 * Tout ici est pur.
 */
import type { ProjectSubcontract } from "./types.js";

export type SubcontractApprovalStatus = "not_presented" | "presented" | "approved" | "refused";

export const SUBCONTRACT_APPROVAL_LABELS: Record<SubcontractApprovalStatus, string> = {
  not_presented: "Pas présenté au client",
  presented: "Présenté, en attente",
  approved: "Agréé par le client",
  refused: "Refusé par le client",
};

export type SubcontractPaymentGuarantee = "none" | "caution" | "delegation" | "direct";

export const PAYMENT_GUARANTEE_LABELS: Record<SubcontractPaymentGuarantee, string> = {
  none: "Aucune",
  caution: "Caution bancaire",
  delegation: "Délégation de paiement",
  direct: "Paiement direct (marché public)",
};

/** Qui a réglé le sous-traitant. */
export type SubcontractPaidBy = "company" | "client";

export const SUBCONTRACT_PAID_BY_LABELS: Record<SubcontractPaidBy, string> = {
  company: "Par l'entreprise",
  client: "Par le client",
};

/** Sous délégation ou paiement direct, c'est le client qui paie le sous-traitant. */
export function clientPaysSubcontractor(guarantee: SubcontractPaymentGuarantee): boolean {
  return guarantee === "delegation" || guarantee === "direct";
}

export type SubcontractWarningKind = "not_presented" | "awaiting_approval" | "refused" | "no_guarantee";

export interface SubcontractWarning {
  kind: SubcontractWarningKind;
  message: string;
}

/**
 * Ce qui manque à un marché au regard de la loi de 1975. Un marché annulé
 * n'engage plus rien ; un marché à signer, si : c'est avant la signature
 * qu'on présente le sous-traitant et qu'on obtient la caution.
 */
export function subcontractWarnings(
  contract: Pick<ProjectSubcontract, "status" | "approvalStatus" | "paymentGuarantee">,
): SubcontractWarning[] {
  if (contract.status === "cancelled") return [];
  const out: SubcontractWarning[] = [];
  if (contract.approvalStatus === "not_presented") {
    out.push({
      kind: "not_presented",
      message: "À présenter au client : sans son agrément, le contrat ne peut pas être opposé au sous-traitant.",
    });
  } else if (contract.approvalStatus === "presented") {
    out.push({ kind: "awaiting_approval", message: "Présenté au client : son agrément est attendu." });
  } else if (contract.approvalStatus === "refused") {
    out.push({ kind: "refused", message: "Le client a refusé ce sous-traitant." });
  }
  if (contract.paymentGuarantee === "none") {
    out.push({
      kind: "no_guarantee",
      message: "Ni caution ni délégation de paiement : le sous-traitant peut faire annuler le contrat.",
    });
  }
  return out;
}

/** Un lien de planning, tel que le bureau le voit. **Sans le jeton.** */
export interface SubcontractPlanningLink {
  id: string;
  projectId: string;
  subcontractId: string;
  expiresAt: string;
  revokedAt: string | null;
  /** Nul tant que personne n'a ouvert le lien. */
  lastSeenAt: string | null;
  viewCount: number;
  createdAt: string;
}

/** Un lien vivant : ni révoqué, ni périmé. */
export function isLivePlanningLink(link: SubcontractPlanningLink, now: Date = new Date()): boolean {
  return link.revokedAt === null && Date.parse(link.expiresAt) > now.getTime();
}

/** Un marché en cours à mettre en règle, tel que le tableau de bord le liste. */
export interface ContractNeedingAttention {
  contractId: string;
  projectId: string;
  projectName: string;
  subcontractorName: string;
  warnings: SubcontractWarning[];
}

/** Le refus d'abord, puis ce qu'il faut faire, puis ce qu'on attend. */
const GRAVITE: Record<SubcontractWarningKind, number> = {
  refused: 0,
  not_presented: 1,
  no_guarantee: 2,
  awaiting_approval: 3,
};

/**
 * Les marchés à signer ou signés auxquels il manque l'agrément ou la
 * garantie. Un marché terminé ou annulé n'est plus à mettre en règle : il
 * reste lisible sous son chantier.
 */
export function contractsNeedingAttention(
  contracts: readonly (Pick<
    ProjectSubcontract,
    "id" | "projectId" | "subcontractorName" | "status" | "approvalStatus" | "paymentGuarantee"
  > & { projectName: string })[],
): ContractNeedingAttention[] {
  const rang = (c: ContractNeedingAttention) => Math.min(...c.warnings.map((w) => GRAVITE[w.kind]));
  return contracts
    .filter((c) => c.status === "draft" || c.status === "signed")
    .map((c) => ({
      contractId: c.id,
      projectId: c.projectId,
      projectName: c.projectName,
      subcontractorName: c.subcontractorName,
      warnings: subcontractWarnings(c).sort((a, b) => GRAVITE[a.kind] - GRAVITE[b.kind]),
    }))
    .filter((c) => c.warnings.length > 0)
    .sort((a, b) => rang(a) - rang(b) || a.projectName.localeCompare(b.projectName, "fr"));
}
